/**
 * Phase 6b1 PR 5–6 — `useRaceResults` hook.
 *
 * PR 5 shipped read-only; PR 6 extends with write methods
 * (`createResult` / `updateResult` / `deleteResult`) + the
 * `pendingPR` freshness check that drives the PR celebration.
 *
 * Data flow:
 *   - GET /api/races[?distance=X]  (the endpoint family shipped
 *     in #50, server-scoped to the authenticated user; PR
 *     derivation is server-side per the same hardening PR).
 *   - POST /api/races               — server stamps user_id +
 *     recomputes is_pr; client never trusts client-supplied flags.
 *   - PUT /api/races/:id            — same ownership enforcement.
 *   - DELETE /api/races/:id         — same ownership enforcement.
 *
 * `pendingPR`: returns `{ distance, time }` for the most-recent
 * race that is `is_pr === true` AND was inserted within the last
 * `PR_CELEBRATION_FRESHNESS_MS` window (default 8 s — wide enough
 * to absorb the form-close transition + list re-render). Cleared
 * either by:
 *   - the celebration `dismissPendingPR()` callback (after the
 *     overlay finishes its 2.4-s sequence)
 *   - a refetch that returns a stale dataset (no fresh PR found)
 *
 * The freshness window is intentional: a refetch on page mount
 * with old data should NOT trigger a celebration ("welcome back,
 * here's a celebration for the PR you set last week"). PR 5
 * always returned `pendingPR: null`; PR 6 wires it up.
 *
 * Loading semantics:
 *   - On first mount with auth ready: loading=true, data=null
 *   - On every refetch: loading=true (chart re-render is cheap)
 *   - Auth not yet ready: loading=false, data=null, error=null
 *
 * Empty vs error:
 *   - `data: []` (empty array) → "no races yet, show empty CTA"
 *   - `error: Error` only on network/server failure → state J.
 *
 * Ownership discipline (safety gate #6 + #8):
 *   - Hook NEVER includes client-supplied user_id on insert.
 *   - Updates / deletes route by row id; server validates
 *     `req.authUser.id === row.user_id` (api/index.js:5216-5224).
 */

import { useCallback, useEffect, useRef, useState } from 'react';
import { useAuthContext } from '../contexts/AuthContext';
import { useAuthFetch } from './useAuthFetch';
import type { RaceResult } from '../lib/racePR';

/** Window for "this PR was just set" — drives celebration trigger. */
const PR_CELEBRATION_FRESHNESS_MS = 8_000;

/**
 * 🐛 B69 (founder, Build 26, on-device) — "adding a race produced a
 * DUPLICATE entry that disappeared after creating another entry."
 *
 * A row the list showed once and then stopped showing is not a row
 * the database ever had twice — it is a RECONCILIATION fault. Three
 * of them lived here, and all three are the same missing idea: this
 * hook had no notion of "which answer is the current one."
 *
 *  1. NO REQUEST-SEQUENCE GUARD. `refetch` applied whatever landed,
 *     whenever it landed. Two GETs overlap routinely on this surface
 *     — the mount effect's, and the one `createResult` awaits — and
 *     on a phone the older one can win the race. The pre-insert list
 *     then overwrites the post-insert list and the new race vanishes;
 *     the NEXT mutation refetches and it is back. Its siblings
 *     `useMeetEntries` and `useMyMeetEntries` both carry a `seqRef`
 *     for exactly this; this hook and `useTeamMeets` were the two
 *     that never got one.
 *
 *  2. NOTHING DEDUPED BY ID. Whatever the server said became the
 *     list verbatim, so any repeated row rendered twice — React keys
 *     by `r.id`, and two identical keys is a duplicate on screen.
 *
 *  3. A THROW *AFTER* THE INSERT LANDED. `createResult` did
 *     `res.json().catch(() => ({}))` and then threw "Server returned
 *     empty race row" when the body was missing. On a 2xx that is a
 *     row the server ALREADY WROTE: the form showed an error, the
 *     athlete pressed Save again, and that second press is a second
 *     real race. The only genuine duplicate-maker in the flow, and
 *     it fires precisely when the network is flaky.
 *
 * The single-flight guard closes the same door from the other side:
 * one Save can now only ever produce one POST, whatever the WebView
 * does with the tap.
 */

/**
 * Last-write-wins by id, first occurrence's POSITION kept.
 *
 * Order is meaning on this surface (the API sorts by `race_date`
 * DESC and `<RaceList>` renders "last 5 + Show more" off the top of
 * it), so a duplicate must collapse in place rather than jump to the
 * end. Exported for the B69 tests.
 */
export function dedupeRacesById(rows: readonly RaceResult[]): RaceResult[] {
  const at = new Map<string, number>();
  const out: RaceResult[] = [];
  for (const r of rows) {
    if (!r || typeof r.id !== 'string') continue;
    const seen = at.get(r.id);
    if (seen === undefined) {
      at.set(r.id, out.length);
      out.push(r);
    } else {
      out[seen] = r;
    }
  }
  return out;
}

/**
 * What makes two create attempts THE SAME create. Deliberately the
 * fields that identify a race to a human — not the whole payload, so
 * a re-render that rebuilds `notes` as `null` vs `''` cannot slip a
 * second POST through.
 */
function createSignature(input: CreateRaceResultInput): string {
  return [input.race_date, input.distance, input.finish_time, input.event_name].join('|');
}

export interface UseRaceResultsArgs {
  /** Optional distance filter — passes through to
   *  `GET /api/races?distance=<distance>`. PR 5 always passed
   *  undefined (route-level filter is client-side on the cached
   *  dataset); PR 6 keeps that pattern. */
  distance?: string;
  /**
   * 2.0 wave 2 (W2-RACE-PAGE): skip the read on mount. For a caller that only WRITES through this hook
   * (the "Add a race" sheet; the page that mounts it owns the list and reads it back itself), so opening
   * the sheet adds no `GET /api/races`. Additive: absent or false, nothing changes. The writes below
   * still refetch this instance after they land, and `refetch()` still works when called.
   */
  skipInitialFetch?: boolean;
}

/** Payload accepted by createResult — mirrors validateRacePayload's
 *  required fields at api/index.js:5260+. user_id + is_pr are
 *  intentionally absent (server is source of truth). */
export interface CreateRaceResultInput {
  race_date: string;            // 'YYYY-MM-DD'
  event_name: string;
  distance: string;
  finish_time: string;          // 'm:ss' / 'mm:ss'
  finish_time_seconds: number;
  place?: number | null;
  total_runners?: number | null;
  course_name?: string | null;
  conditions?: string | null;
  notes?: string | null;
  season_id?: string | null;
}

export type UpdateRaceResultInput = Partial<CreateRaceResultInput>;

/**
 * The just-set PR the celebration renders.
 *
 * ⛔ B68 — `seconds` is carried HERE, from the row the server just
 * returned, because the route used to re-derive it by `find()`-ing
 * the new race back out of `data` and would not mount the
 * celebration until that lookup succeeded. That made a 2.4-second
 * animation's existence depend on a list being replaced underneath
 * it. The signal is self-contained now.
 */
export interface PendingPR {
  distance: string;
  time: string;
  /** The new finish time in seconds — the celebration's delta input. */
  seconds: number;
}

export interface UseRaceResultsResult {
  data: RaceResult[] | null;
  pendingPR: PendingPR | null;
  loading: boolean;
  error: Error | null;
  refetch: () => Promise<void>;
  /**
   * Insert a new race result. Returns the saved row (server may flip
   * is_pr after recompute), or `null` when the insert landed but the
   * response body did not arrive intact — see B69 note 3: on a 2xx
   * the row EXISTS, and throwing there is how one Save becomes two
   * races. Throws only when the write itself failed.
   *
   * Single-flight: while an identical create is in flight, a second
   * call joins the first instead of issuing a second POST.
   */
  createResult: (input: CreateRaceResultInput) => Promise<RaceResult | null>;
  /** Patch an existing race result by id. Throws on error. */
  updateResult: (id: string, patch: UpdateRaceResultInput) => Promise<RaceResult>;
  /** Delete a race result by id. The UI MUST route this through
   *  `<DeleteConfirmDialog>` per safety gate #7 — the hook itself
   *  doesn't enforce confirmation, but the route always wraps. */
  deleteResult: (id: string) => Promise<void>;
  /** Clear the pendingPR signal. Called by `<PRCelebration>` after
   *  its 2.4-s overlay sequence. */
  dismissPendingPR: () => void;
}

interface ApiResponse {
  races: RaceResult[];
  error?: string;
}

interface ApiCreateResponse {
  race?: RaceResult;
  error?: string;
}

export function useRaceResults({ distance, skipInitialFetch }: UseRaceResultsArgs = {}): UseRaceResultsResult {
  const { user, loading: authLoading } = useAuthContext();
  const fetchAuthed = useAuthFetch();
  const userId = user?.id ?? null;

  const [data, setData] = useState<RaceResult[] | null>(null);
  const [loading, setLoading] = useState<boolean>(!skipInitialFetch);
  const [error, setError] = useState<Error | null>(null);
  const [pendingPR, setPendingPR] = useState<PendingPR | null>(null);

  // Tracks the timestamp of the most recent successful create that
  // returned is_pr === true. Used to gate `pendingPR` so a remount
  // / refetch doesn't replay celebrations.
  const lastPrCreatedAt = useRef<number | null>(null);
  const lastPrSignature = useRef<string | null>(null);

  /**
   * ⛔ B69 note 1 — WHICH ANSWER IS THE CURRENT ONE.
   *
   * Every read takes a ticket; only the newest ticket may write to
   * state. Without this, the mount effect's GET and the GET that
   * `createResult` awaits are two racers with no finish line, and an
   * older answer landing last erases the race the athlete just added.
   */
  const seqRef = useRef(0);

  /** In-flight creates, keyed by what makes them the same create. */
  const inFlightCreates = useRef<Map<string, Promise<RaceResult | null>>>(new Map());

  const refetch = useCallback(async () => {
    if (authLoading) return;
    const seq = ++seqRef.current;
    if (!userId) {
      setData(null);
      setLoading(false);
      setError(null);
      return;
    }
    setLoading(true);
    setError(null);
    try {
      const url = distance
        ? `/api/races?distance=${encodeURIComponent(distance)}`
        : '/api/races';
      const res = await fetchAuthed(url, { method: 'GET' });
      if (!res.ok) {
        const text = await res.text().catch(() => '');
        throw new Error(`GET ${url} failed: ${res.status} ${text || res.statusText}`);
      }
      const body = (await res.json()) as ApiResponse;
      if (body.error) throw new Error(body.error);
      // A superseded read is not wrong, it is just OLD. Drop it whole —
      // including its loading flag, which belongs to the read still running.
      if (seq !== seqRef.current) return;
      setData(dedupeRacesById(Array.isArray(body.races) ? body.races : []));
    } catch (err) {
      if (seq !== seqRef.current) return;
      setData(null);
      setError(err instanceof Error ? err : new Error(String(err)));
    } finally {
      if (seq === seqRef.current) setLoading(false);
    }
  }, [authLoading, userId, distance, fetchAuthed]);

  useEffect(() => {
    if (skipInitialFetch) return;
    void refetch();
  }, [refetch, skipInitialFetch]);

  const createResult = useCallback(
    (input: CreateRaceResultInput): Promise<RaceResult | null> => {
      if (!userId) return Promise.reject(new Error('Not signed in'));

      /**
       * ⛔ B69 — ONE SAVE, ONE POST. The form's own `status === 'saving'`
       * guard depends on a React state update having reached the button
       * before the second activation arrives; that is a race, and it is
       * the WebView's to win. This guard is not: an identical create
       * already in flight is JOINED, never re-sent.
       */
      const sig = createSignature(input);
      const already = inFlightCreates.current.get(sig);
      if (already) return already;

      const run = (async (): Promise<RaceResult | null> => {
        const res = await fetchAuthed('/api/races', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(input),
        });
        const body = (await res.json().catch(() => null)) as ApiCreateResponse | null;
        if (!res.ok || body?.error) {
          throw new Error(body?.error || `POST /api/races failed: ${res.status}`);
        }
        const saved = body?.race ?? null;
        // Track freshness so pendingPR fires for THIS create only.
        // The signature is `id|distance|time` so a refetch landing
        // the same row doesn't re-trigger.
        if (saved?.is_pr === true) {
          lastPrCreatedAt.current = Date.now();
          lastPrSignature.current = `${saved.id}|${saved.distance}|${saved.finish_time}`;
          setPendingPR({
            distance: saved.distance,
            time: saved.finish_time,
            seconds: saved.finish_time_seconds,
          });
        }
        // Refresh the cache so PRChart + RaceList + PRBadges all see
        // the new row + any flips of is_pr that the server-side
        // recompute did on adjacent rows.
        //
        // ⛔ B69 note 3 — this runs even when `saved` is null. A 2xx with
        // an unreadable body is still a row the server WROTE; the honest
        // move is to go and read it back, not to raise an error that
        // invites a second Save and a second race.
        await refetch();
        return saved;
      })();

      inFlightCreates.current.set(sig, run);
      return run.finally(() => {
        inFlightCreates.current.delete(sig);
      });
    },
    [userId, fetchAuthed, refetch],
  );

  const updateResult = useCallback(
    async (id: string, patch: UpdateRaceResultInput): Promise<RaceResult> => {
      if (!userId) throw new Error('Not signed in');
      if (!id) throw new Error('Race id required');
      const res = await fetchAuthed(`/api/races/${encodeURIComponent(id)}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(patch),
      });
      const body = (await res.json().catch(() => ({}))) as ApiCreateResponse;
      if (!res.ok || body.error) {
        throw new Error(body.error || `PUT /api/races/${id} failed: ${res.status}`);
      }
      if (!body.race) throw new Error('Server returned empty race row');
      // Updates do NOT trigger a celebration — vanilla parity (only
      // first-insert PRs celebrate; if you edit your time DOWN to a
      // new PR after the fact, no overlay).
      await refetch();
      return body.race;
    },
    [userId, fetchAuthed, refetch],
  );

  const deleteResult = useCallback(
    async (id: string): Promise<void> => {
      if (!userId) throw new Error('Not signed in');
      if (!id) throw new Error('Race id required');
      const res = await fetchAuthed(`/api/races/${encodeURIComponent(id)}`, {
        method: 'DELETE',
      });
      if (!res.ok) {
        const body = (await res.json().catch(() => ({}))) as ApiCreateResponse;
        throw new Error(body.error || `DELETE /api/races/${id} failed: ${res.status}`);
      }
      await refetch();
    },
    [userId, fetchAuthed, refetch],
  );

  const dismissPendingPR = useCallback(() => {
    setPendingPR(null);
  }, []);

  // Defensive: if the freshness window expires without dismissal
  // (e.g., the overlay errored out), clear pendingPR so a remount
  // doesn't replay it. The PR celebration spec budgets ~2.7 s; 8 s
  // is a generous ceiling.
  useEffect(() => {
    if (!pendingPR) return;
    const created = lastPrCreatedAt.current ?? 0;
    const elapsed = Date.now() - created;
    const remaining = Math.max(0, PR_CELEBRATION_FRESHNESS_MS - elapsed);
    const t = setTimeout(() => setPendingPR(null), remaining);
    return () => clearTimeout(t);
  }, [pendingPR]);

  return {
    data,
    pendingPR,
    loading,
    error,
    refetch,
    createResult,
    updateResult,
    deleteResult,
    dismissPendingPR,
  };
}

// Exported for unit tests of the freshness gate.
export const __testing__ = {
  PR_CELEBRATION_FRESHNESS_MS,
};
